import { MouseEventHandler, ReactNode } from "react";

import { Theme, Typography, TypographyProps } from "@mui/material";
import { Tooltip, TooltipProps } from "@shared/Tooltip";
import { useIsClipped } from "@shared/useIsClipped";

const baseSx = (theme: Theme) => ({
    cursor: "pointer",
    color: theme.palette.emteria.link,
    textDecoration: "none",
    // resets native <button> chrome for component="button" usages; a no-op for span/div
    border: "none",
    background: "none",
    padding: 0,
    margin: 0,
    font: "inherit",
    textAlign: "left",
    "&:hover": {
        color: theme.palette.emteria.warning,
    },
});

export type TextLinkProps = {
    children?: ReactNode;
    onClick?: MouseEventHandler<HTMLElement>;
    tooltip: string;
    /** Renders a real anchor, so the link stays middle-clickable and "open in new tab" works. */
    href?: string;
    target?: string;
    component?: TypographyProps["component"];
    variant?: TypographyProps["variant"];
    type?: "button" | "submit" | "reset";
    className?: string;
    sx?: TypographyProps["sx"];
    placement?: TooltipProps["placement"];
    truncatedTooltip?: string;
};

const truncateSx = {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

/**
 * The house style for a clickable word/number embedded in surrounding text: blue, turns
 * orange on hover, never underlined, and always paired with a tooltip explaining the click.
 */
export const TextLink = ({
    children,
    onClick,
    tooltip,
    href,
    target,
    component = href ? "a" : "span",
    variant = "inherit",
    type,
    className,
    sx = {},
    placement = "top",
    truncatedTooltip,
}: TextLinkProps) => {
    const truncate = truncatedTooltip !== undefined;
    const { ref, clipped } = useIsClipped(truncatedTooltip);

    const typographyProps = {
        component,
        variant,
        type,
        onClick,
        className,
        href,
        target,
        rel: href && target === "_blank" ? "noopener noreferrer" : undefined,
        sx: truncate ? [baseSx, truncateSx, sx] : [baseSx, sx],
        ref: truncate ? ref : undefined,
    } as TypographyProps;

    return (
        <Tooltip
            title={clipped && truncatedTooltip ? truncatedTooltip : tooltip}
            placement={placement}
            childrenBoxStyles={truncate ? { display: "block", minWidth: 0 } : undefined}
        >
            <Typography {...typographyProps}>{children}</Typography>
        </Tooltip>
    );
};
